import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../../axiosinterceptor";
import { User } from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   INITIAL FILTER SHAPES
═══════════════════════════════════════════════════════════ */

export const initialOutstandingFilters = {
  episode_identifier: [],
  patient_id: [],
  priority: [],
  status: [],
  episode_type: [],
  assign_coder: [],
  current_queue: [],
  queue_type: [],
  speciality: [],
  days_of_outstanding: [],
  admission_date_from: null,
  admission_date_to: null,
  discharge_date_from: null,
  discharge_date_to: null,
  los: [],
  los_op: "",
  sort_by: "",
  sort_order: "",
  search_term :"",
  search:""
};

export const initialDRGFilters = {
  search: "",
  drg_search: "",
  admission_date_from: null,
  admission_date_to: null,
  separation_date_from: null,
  separation_date_to: null,
  los: [],
  los_op: "",
  speciality: [],
  principal_diagnosis_code: [],
  episode_id: [],
  drg_code: [],
  description: [],
  alos: [],
  lower_bound: [],
  upper_bound: [],
  status: [],
  codes_assigned: [],
  sort_by: "",
  sort_order: "",
  revenue:[]
};

export const initialHACFilters = {
  episode_id: [],
  admission_date_from: null,
  admission_date_to: null,
  discharge_date_from: null,
  discharge_date_to: null,
  search: "",
  los: [],
  los_op: "",
  speciality: [],
  status: [],
  drg: [],
  hac_code: [],
  hac_description: [],
  priority: [],
  nwau: [],
  revenue_dollar: [],
  sort_by: "",
  sort_order: "",
};

export const initialErrorDrgFilters = {
  episode_id: [],
  admission_date_from: null,
  admission_date_to: null,
  separation_date_from: null,
  separation_date_to: null,
  episode_type: [],
  speciality: [],
  status: [],
  drg_code: [],
  description: [],
  principal_diagnosis: [],
  nwau: [],
  revenue_dollar: [],
  los: [],
  los_op: "",
  sort_by: "",
  sort_order: "",
};
export const initialCodingKpisFilters = {
  user_id: [],
  start_date: null,
  end_date: null,
  episodes_coded:[],
  coding_days: [],
  coding_days_operator:"",
  site:[],
  total_episodes_coded:[],
  total_episodes_coded_operator:"",
  manually_coded:[],
  manually_coded_operator:"",
  ai_validation_coded:[],
  ai_validation_coded_operator:"",
  nwau: [],
  average_los: [],
  average_los_operator:"",
  sort_by: "",
  sort_order: "",
};

export const initialCoderKpisFilters = {
  user_id: [],
  start_date: null,
  end_date: null,
  site: [],
  speciality: [],
  episode_id:[],
  drg: [],
  validation: [],
  separation_date_to:[],
  separation_date_from:[],
  no_of_codes: [],
  no_of_codes_operator: "",
  los: [],
  los_operator: "",
  nwau: [],
  nwau_operator: "",
  sort_by: "",
  sort_order: "",
};
export const initialMissingMvdsFilters = {
  missing_notes_flag: "",
  document_type: [],
  health_fund: [],
  discharge_date_from: null,
  discharge_date_to: null,
  status: [],
  episode_id: [],
  medical_record_number: [],
  specialty: [],
  search: "",
  sort_by: "",
  sort_order: "",
};


const initialAppliedFilters = {
  "/outstanding-report": { ...initialOutstandingFilters },
  "/drg-outlier-report": { ...initialDRGFilters },
  "/hac-report": { ...initialHACFilters },
  "/error-drg-report": { ...initialErrorDrgFilters },
  "/coding-kpis-report":{...initialCodingKpisFilters},
  "/codingkpis-coder-report":{...initialCoderKpisFilters},
  "/missing-mvds-report": { ...initialMissingMvdsFilters }

};

/* ═══════════════════════════════════════════════════════════
   RESPONSE HELPERS
═══════════════════════════════════════════════════════════ */

/** Follow a dot-separated path in an object */
const resolveDotPath = (obj, path) =>
  path ? path.split(".").reduce((acc, k) => acc?.[k], obj) : undefined;

/**
 * extractFilterData
 * Extracts the data array from a filter API response.
 * Handles flat, body-wrapped, and fallback (first array value) shapes.
 */
const extractFilterData = (raw, responseKey) => {
  if (!raw) return [];

  // 1. Try direct dot-path on root
  const direct = resolveDotPath(raw, responseKey);
  if (Array.isArray(direct)) return direct;

  // 2. HAC-style: { statusCode, body: { hac_code: [...] } }
  if (raw.body) {
    const fromBody = resolveDotPath(raw.body, responseKey);
    if (Array.isArray(fromBody)) return fromBody;
  }

  // 3. Fallback: return the first array value (skip meta keys)
  const META = new Set([
    "has_more",
    "hasmore",
    "page_number",
    "limit",
    "statusCode",
    "message",
    "headers",
  ]);
  const scan = (obj) => {
    for (const [k, v] of Object.entries(obj || {})) {
      if (!META.has(k) && Array.isArray(v)) return v;
    }
    return null;
  };
  return scan(raw) || scan(raw.body) || [];
};

/** Read has_more / hasmore from various response shapes */
// const extractHasMore = (raw, incoming, limit) => {
//   if (!raw) return incoming.length >= limit;
//   if (typeof raw.has_more === "boolean") return raw.has_more;
//   if (typeof raw.hasmore === "boolean") return raw.hasmore;
//   if (raw.body) return extractHasMore(raw.body, incoming, limit);
//   return incoming.length >= limit;
// };
const extractHasMore = (raw, incoming, limit) => {
  if (!raw) return incoming.length >= limit;

  if (typeof raw.has_more === "boolean") return raw.has_more;

  if (
    raw.queues &&
    typeof raw.queues.has_more === "boolean"
  ) {
    return raw.queues.has_more;
  }

  if (raw.body) {
    return extractHasMore(raw.body, incoming, limit);
  }

  return incoming.length >= limit;
};

/* ═══════════════════════════════════════════════════════════
   GENERIC THUNKS  (reusable for every report)
═══════════════════════════════════════════════════════════ */

/**
 * fetchReportData — generic report data fetch, keyed by reportPath
 */
export const fetchReportData = createAsyncThunk(
  "reports/fetchReportData",
  async ({ reportPath, endpoint, method = "POST", payload }, thunkAPI) => {
    if (!endpoint) {
      // Called from somewhere without a valid endpoint (e.g. HIM assign modal) — skip silently
      return thunkAPI.rejectWithValue({ reportPath, error: "Missing endpoint" });
    }
    try {
      const res =
        method.toUpperCase() === "GET"
          ? await axiosInstance.get(endpoint, { params: payload })
          : await axiosInstance.post(endpoint, payload);
      return { reportPath, data: res.data };
    } catch (err) {
      return thunkAPI.rejectWithValue({
        reportPath,
        error: err.response?.data || "Failed to fetch report",
      });
    }
  },
);

/**
 * exportReportData — generic export, returns raw data so caller grabs download_url
 */
export const exportReportData = createAsyncThunk(
  "reports/exportReportData",
  async ({ reportPath, endpoint, method = "POST", payload }, thunkAPI) => {
    try {
      const res =
        method.toUpperCase() === "GET"
          ? await axiosInstance.get(endpoint, { params: payload })
          : await axiosInstance.post(endpoint, payload);
      return { reportPath, data: res.data };
    } catch (err) {
      return thunkAPI.rejectWithValue({
        reportPath,
        error: err.response?.data || "Failed to export",
      });
    }
  },
);

/**
 * fetchColumnFilterOptions
 * One thunk for ALL column-header and modal filter dropdowns.
 *
 * Params:
 *   reportPath  — "/outstanding-report" etc.
 *   columnKey   — key in columnFilterOptions[reportPath], e.g. "episode_identifier"
 *   endpoint    — API path, e.g. "/him_episode_identifier_filters_acs"
 *   responseKey — dot-path to array inside response, e.g. "episode_identifier_number"
 *   search      — debounced search string (default "")
 *   page        — 1 = load fresh, >1 = append more
 *   limit       — items per request (default 10)
 */
export const fetchColumnFilterOptions = createAsyncThunk(
  "reports/fetchColumnFilterOptions",
  async (
    {
      reportPath,
      columnKey,
      endpoint,
      responseKey = "",
      search = "",
      page = 1,
      limit = 10,
    },
    thunkAPI,
  ) => {
    try {
      const res = await axiosInstance.post(endpoint, {
        search,
        page_number: page,
        limit,
      });
      return {
        reportPath,
        columnKey,
        responseKey,
        data: res.data,
        page,
        limit,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue({
        reportPath,
        columnKey,
        error: "Failed",
      });
    }
  },
);

/* ═══════════════════════════════════════════════════════════
   DRG LOWER-BOUND THUNK
   Kept separate — ReportTable uses MRT autocomplete for this column
═══════════════════════════════════════════════════════════ */

// export const fetctLowerBondData = createAsyncThunk(
//   "reports/fetctLowerBondData",
//   async (payload, thunkAPI) => {
//     try {
//       const res = await axiosInstance.post("/LowerBoundFilter", payload);
//       return res.data;
//     } catch (err) {
//       return thunkAPI.rejectWithValue(err.response?.data || "Failed");
//     }
//   },
// );

/* ═══════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════ */
const ReportsSlice = createSlice({
  name: 'reports',

  initialState: {
    /* ── Report data — keyed by reportPath ── */
    reportData: {},
    reportLoading: {},
    reportError: {},

    /* ── Export — keyed by reportPath ── */
    exportLoading: {},
    exportError: {},

    /* ── Applied filters — keyed by reportPath ── */
    appliedFilters: { ...initialAppliedFilters },

    /*
     * columnFilterOptions — keyed: [reportPath][columnKey]
     * Each entry: { data: any[], loading: bool, isLoaded: bool, hasMore: bool, page: number }
     */
    columnFilterOptions: {},

    /* ── Single modal flag (only one report visible at a time) ── */
    showFilterModal: false,

    /* ── DRG lower-bound autocomplete (ReportTable MRT column) ── */
    // lowerBondData:        [],
    // lowerBondHasMore:     true,
    // lowerBondPage:        1,
    // lowerBondDataloading: false,
    // lowerBondError:       null,
    //coding kpis filter and report
    showCodingKpisReport: false,
  },

  reducers: {
    /** Set a single filter value for a report */
    updateAppliedFilter(state, { payload: { reportPath, key, value } }) {
      if (!state.appliedFilters[reportPath]) state.appliedFilters[reportPath] = {};
      state.appliedFilters[reportPath][key] = value;
    },
    // clearReportData(state, { payload: { reportPath } }) {
    //   delete state.reportData[reportPath];
    //   state.reportLoading[reportPath] = false;
    //   state.reportError[reportPath] = null;
    // },

    /** Set multiple filter values at once */
    batchUpdateAppliedFilters(state, { payload: { reportPath, filters } }) {
      if (!state.appliedFilters[reportPath]) state.appliedFilters[reportPath] = {};
      Object.assign(state.appliedFilters[reportPath], filters);
    },

    /** Reset all filters for a report to their initial state */
    resetAppliedFilters(state, { payload: { reportPath } }) {
      state.appliedFilters[reportPath] = {
        ...(initialAppliedFilters[reportPath] || {}),
      };
    },

    setShowFilterModal(state, { payload }) {
      state.showFilterModal = payload;
    },

    clearColumnFilterOptions(state, { payload: { reportPath } = {} }) {
      if (reportPath) delete state.columnFilterOptions[reportPath];
      else state.columnFilterOptions = {};
    },

    /* ── Legacy reducer aliases — kept so older components still compile ── */
    setDRGReportFilters(state, { payload: { key, value } }) {
      if (!state.appliedFilters['/drg-outlier-report'])
        state.appliedFilters['/drg-outlier-report'] = { ...initialDRGFilters };
      state.appliedFilters['/drg-outlier-report'][key] = value;
    },
    resetDRGFilters(state) {
      state.appliedFilters['/drg-outlier-report'] = { ...initialDRGFilters };
    },
    setHACReportFilters(state, { payload: { key, value } }) {
      if (!state.appliedFilters['/hac-report'])
        state.appliedFilters['/hac-report'] = { ...initialHACFilters };
      state.appliedFilters['/hac-report'][key] = value;
    },
    resetHACFilters(state) {
      state.appliedFilters['/hac-report'] = { ...initialHACFilters };
    },
    setErrorDrgFilter(state, { payload: { key, value } }) {
      if (!state.appliedFilters['/error-drg-report'])
        state.appliedFilters['/error-drg-report'] = {
          ...initialErrorDrgFilters,
        };
      state.appliedFilters['/error-drg-report'][key] = value;
    },
    setErrorDrgDateRange(state, { payload: { from, to } }) {
      const f = state.appliedFilters['/error-drg-report'];
      if (f) {
        f.separation_date_from = from;
        f.separation_date_to = to;
      }
    },
    resetErrorDrgFilters(state) {
      state.appliedFilters['/error-drg-report'] = { ...initialErrorDrgFilters};
    },
    setCodingKPISReportFilters(state, { payload: { key, value } }) {
      if (!state.appliedFilters['/coding-kpis-report'])
        state.appliedFilters['/coding-kpis-report'] = { ...initialCodingKpisFilters};
      state.appliedFilters['/coding-kpis-report'][key] = value;
    },
    resetCoidngKPISFilters(state) {
      state.appliedFilters['/coding-kpis-report'] = { ...initialCodingKpisFilters};
    },
     setCoderKPISReportFilters(state, { payload: { key, value } }) {
      if (!state.appliedFilters['/codingkpis-coder-report'])
        state.appliedFilters['/codingkpis-coder-report'] = { ...initialCoderKpisFilters};
      state.appliedFilters['/codingkpis-coder-report'][key] = value;
    },
    resetCoderKPISFilters(state) {
      state.appliedFilters['/codingkpis-coder-report'] = { ...initialCoderKpisFilters};
    },
    resetReportState(state, { payload: { reportPath } }) {
      state.appliedFilters[reportPath] = {
        ...(initialAppliedFilters[reportPath] || {}),
      };
      delete state.reportData[reportPath]; 
      state.reportLoading[reportPath] = false;
      state.reportError[reportPath] = null;
      delete state.columnFilterOptions[reportPath];
    },
    //coding kpis report and filter
     setShowCodingKpisReport: (state, action) => {
      state.showCodingKpisReport = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ──────────── fetchReportData ──────────── */
      .addCase(fetchReportData.pending, (state, { meta }) => {
        state.reportLoading[meta.arg.reportPath] = true;
        state.reportError[meta.arg.reportPath] = null;
      })
      .addCase(fetchReportData.fulfilled, (state, { payload }) => {
        state.reportLoading[payload.reportPath] = false;
        state.reportData[payload.reportPath] = payload.data;
      })
      .addCase(fetchReportData.rejected, (state, { payload, meta }) => {
        const p = payload?.reportPath || meta.arg?.reportPath;
        if (p) {
          state.reportLoading[p] = false;
          state.reportError[p] = payload?.error || 'Error';
        }
      })

      /* ──────────── exportReportData ──────────── */
      .addCase(exportReportData.pending, (state, { meta }) => {
        state.exportLoading[meta.arg.reportPath] = true;
        state.exportError[meta.arg.reportPath] = null;
      })
      .addCase(exportReportData.fulfilled, (state, { payload }) => {
        state.exportLoading[payload.reportPath] = false;
      })
      .addCase(exportReportData.rejected, (state, { meta }) => {
        const p = meta.arg?.reportPath;
        if (p) state.exportLoading[p] = false;
      })

      /* ──────────── fetchColumnFilterOptions ──────────── */
      .addCase(fetchColumnFilterOptions.pending, (state, { meta }) => {
        const { reportPath, columnKey } = meta.arg;
        if (!state.columnFilterOptions[reportPath]) state.columnFilterOptions[reportPath] = {};
        const cur = state.columnFilterOptions[reportPath][columnKey];
        state.columnFilterOptions[reportPath][columnKey] = {
          data: cur?.data || [],
          loading: true,
          isLoaded: cur?.isLoaded || false,
          hasMore: cur?.hasMore ?? true,
          page: cur?.page || 0,
        };
      })
      .addCase(fetchColumnFilterOptions.fulfilled, (state, { payload }) => {
        const { reportPath, columnKey, responseKey, data, page, limit } = payload;
        if (!state.columnFilterOptions[reportPath]) state.columnFilterOptions[reportPath] = {};

        const incoming = extractFilterData(data, responseKey);
        const existing = state.columnFilterOptions[reportPath][columnKey]?.data || [];

        state.columnFilterOptions[reportPath][columnKey] = {
          data: page === 1 ? incoming : [...existing, ...incoming],
          loading: false,
          isLoaded: true,
          hasMore: extractHasMore(data, incoming, limit),
          page,
        };
      })
      .addCase(fetchColumnFilterOptions.rejected, (state, { payload, meta }) => {
        const { reportPath, columnKey } = payload || meta.arg || {};
        if (reportPath && columnKey)
          state.columnFilterOptions[reportPath]?.[columnKey] &&
            (state.columnFilterOptions[reportPath][columnKey].loading = false);
      });

    //   /* ──────────── fetctLowerBondData ──────────── */
    //   .addCase(fetctLowerBondData.pending, (state) => {
    //     state.lowerBondDataloading = true;
    //     state.lowerBondError       = null;
    //   })
    //   .addCase(fetctLowerBondData.fulfilled, (state, { payload }) => {
    //     state.lowerBondDataloading = false;
    //     const incoming = Array.isArray(payload?.lower_bound) ? payload.lower_bound : [];
    //     const page     = payload?.page_number ?? 1;
    //     state.lowerBondData =
    //       page === 1
    //         ? incoming
    //         : Array.from(new Set([...state.lowerBondData, ...incoming]));
    //     state.lowerBondHasMore = payload?.hasmore ?? incoming.length >= 10;
    //     state.lowerBondPage    = page;
    //   })
    //   .addCase(fetctLowerBondData.rejected, (state, { payload }) => {
    //     state.lowerBondDataloading = false;
    //     state.lowerBondError       = payload;
    //   });
  },
});

export const {
  updateAppliedFilter,
  batchUpdateAppliedFilters,
  resetAppliedFilters,
  setShowFilterModal,
  clearColumnFilterOptions,
  setDRGReportFilters,
  resetDRGFilters,
  setHACReportFilters,
  resetHACFilters,
  setErrorDrgFilter,
  setErrorDrgDateRange,
  resetErrorDrgFilters,
  setCodingKPISReportFilters,
  resetCoidngKPISFilters,
  appliedFilters,
  setShowCodingKpisReport,
  setCoderKPISReportFilters,
  resetCoderKPISFilters,
  // clearReportData
  resetReportState
} = ReportsSlice.actions;

export default ReportsSlice.reducer;
